// scheduling/services/schedApi.js
// Write service for scheduler mutations — mirrors client/src/services/api.js pattern

const SCHED_BASE = '/api/schedWrite';

// ── Stage 28D: Cold-start retry layer ───────────────────────────────────────
// Azure SWA-managed Functions spin down after idle. First request of the day
// pays ~3-8s cold-start overhead, and the SWA edge proxy can return a gateway
// 5xx during that window. This wrapper retries WRITE calls ONCE when:
//   • response is a 502/503/504 gateway error AND TTFB < 5s (edge/proxy cold
//     start — the app never ran, so re-sending is safe)
//   • OR fetch throws within 5s of dispatch (network/connection-level)
// A plain 500 is NOT retried: it means the function ran and returned an
// application error (e.g. a validation or UNIQUE-KEY failure), so re-sending
// would just re-fail AND double-log it in Feedback.ServerErrors. Skips retry on
// success, on non-5xx, on 500, and on slow gateway 5xx (likely real).
//
// Reads stay unwrapped — they're idempotent and less catastrophic on hiccup.
// The retry dispatches an `app:cold-start-retry` event so AppShell can show
// a non-blocking "Server warming up — retrying…" toast.
export async function fetchWithColdStartRetry(url, options, { label } = {}) {
  const COLD_START_THRESHOLD_MS = 5000;
  const RETRY_DELAY_MS = 2000;

  const start = (typeof performance !== 'undefined' ? performance.now() : Date.now());

  function notifyRetry() {
    try {
      window.dispatchEvent(new CustomEvent('app:cold-start-retry', { detail: { label } }));
    } catch (_) { /* SSR/no-window — ignore */ }
  }

  let r;
  try {
    r = await fetch(url, options);
  } catch (networkErr) {
    const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - start;
    if (elapsed < COLD_START_THRESHOLD_MS) {
      notifyRetry();
      await new Promise(res => setTimeout(res, RETRY_DELAY_MS));
      return await fetch(url, options);
    }
    throw networkErr;
  }

  if (!r.ok && (r.status === 502 || r.status === 503 || r.status === 504)) {
    const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - start;
    if (elapsed < COLD_START_THRESHOLD_MS) {
      notifyRetry();
      await new Promise(res => setTimeout(res, RETRY_DELAY_MS));
      return await fetch(url, options);
    }
  }

  return r;
}

async function schedWrite(action, body = {}) {
  const url = `${SCHED_BASE}?action=${encodeURIComponent(action)}`;
  const r = await fetchWithColdStartRetry(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }, { label: `schedWrite(${action})` });
  if (!r.ok) {
    const raw = await r.text();
    // The API returns { error, conflict?, detail? } as JSON. Surface `error` on its own
    // for a 409 (an expected uniqueness conflict — the message is already written for the
    // user, so prefixing it with "schedWrite(bulk_move) 409: {...}" just leaks JSON into
    // the toast). Everything else keeps the action+status prefix, which is what makes
    // unexpected failures diagnosable from a screenshot.
    let parsed = null;
    try { parsed = JSON.parse(raw); } catch (_) { /* non-JSON body — fall through */ }
    const e = new Error(
      r.status === 409 && parsed?.error
        ? parsed.error
        : `schedWrite(${action}) ${r.status}: ${parsed?.error || raw}`
    );
    e.status = r.status;
    e.conflict = r.status === 409 || !!parsed?.conflict;
    e.detail = parsed?.detail || null;
    throw e;
  }
  const json = await r.json();
  // Outlook calendar sync (Trello #133): every schedule-changing action returns
  // affectedStaffIds. Fire the reconcile FIRE-AND-FORGET so a sync hiccup can never
  // affect the save. Opt-in + which staff actually sync is decided server-side. This is
  // the single client trigger point — no per-handler wiring, so no path is missed.
  if (json && Array.isArray(json.affectedStaffIds) && json.affectedStaffIds.length) {
    reconcileOutlook(json.affectedStaffIds);
  }
  return json;
}

export const upsertEntry = (entry) => schedWrite('upsert_entry', entry);
export const deleteEntry = (entryId, opts = {}) => schedWrite('delete_entry', { entryId, ...opts });
// Batched delete. Prefer this over looping deleteEntry: each deleteEntry call is 4 DB
// round trips AND triggers its own Outlook reconcile, so an N-day block used to cost
// 4N round trips and N reconciles. This is one call, ~4 round trips, one reconcile.
export const bulkDeleteEntries = (entryIds, opts = {}) => schedWrite('bulk_delete_entries', { entryIds, ...opts });
// Clear the future-dated entries stranded on a job PE has completed. The server resolves
// which entries those are from jobId alone — the grid window and My Schedule's rolling four
// weeks both stop well short of the far-future rows this is meant to catch, so passing ids
// from the client would silently under-delete. Call with { preview: true } first to get
// { entries, hours, staff[] } for the confirm dialog; pass staffId to scope to one person.
export const deleteClosedJobFuture = (jobId, opts = {}) => schedWrite('delete_closed_job_future', { jobId, ...opts });
export const moveEntry = (entryId, updates) => schedWrite('move_entry', { entryId, ...updates });
// opts: { deleteEntryIds?, batchId?, source? } — deleteEntryIds removes the edited block's old
// days in the SAME transaction as the inserts (custom-job edit), so the edit is all-or-nothing.
export const bulkEntries = (entries, createdBy, opts = {}) => schedWrite('bulk_entries', { entries, createdBy, ...opts });
export const bulkMove = (data) => schedWrite('bulk_move', data);
export const bulkMoveMulti = (data) => schedWrite('bulk_move_multi', data);
export const addDependency = (pred, dep) => schedWrite('add_dependency', { predecessorJobId: pred, dependentJobId: dep });
export const removeDependency = (depId) => schedWrite('remove_dependency', { dependencyId: depId });
export const addComment = (data) => schedWrite('add_comment', data);
export const updateExperience = (data) => schedWrite('update_experience', data);
export const markNotifRead = (notifId) => schedWrite('mark_read', { notifId });
export const markAllNotifsRead = (staffId) => schedWrite('mark_all_read', { staffId });
export const notifyRemoval = (staffId, jobId, removedBy) => schedWrite('notify_removal', { staffId, jobId, removedBy });
export const upsertCalendar = (data) => schedWrite('upsert_calendar', data);
export const deleteCalendar = (templateId, staffId) => schedWrite('delete_calendar', { templateId, staffId });
// Item 9 — link staff to a default calendar template (or pass templateId=null to clear).
export const upsertStaffDefaultTemplate = (staffId, templateId) => schedWrite('upsert_staff_default_template', { staffId, templateId });
// Trello #332 — adopt PE's work profile for one staffer. Only a staffId is sent: the
// server re-derives the intent from live PE + RPRS state, so a stale drift panel can't
// apply a decision based on data that has since moved.
export const acceptPECapacity = (staffId) => schedWrite('accept_pe_capacity', { staffId });
export const setJobOnSite = ({ jobId, isOnSite }) => schedWrite('set_job_onsite', { jobId, isOnSite });
// Bug triage: after func-shared-services authoritatively resolves a report,
// notify the reporter(s) in-app. bugs: [{ bugReportId, submittedBy, title, resolutionNote, status }]
export const notifyBugResolved = (bugs) => schedWrite('notify_bug_resolved', { bugs });
// Suggestion triage: after func-shared-services advances a suggestion, notify the
// submitter(s) in-app. suggestions: [{ suggestionId, submittedBy, title, resolutionNote, status }]
export const notifySuggestionUpdated = (suggestions) => schedWrite('notify_suggestion_updated', { suggestions });
export const rollForward = (data) => schedWrite('roll_forward', data);
export const saveFilter = (data) => schedWrite('save_filter', data);
// Edit an existing saved filter in place. Only send what changed:
// { filterId, name?, memberIds?, visibility?, sharedWith? }. Unlike saveFilter
// (keyed on name) this can RENAME, which is why the manage screen needs it.
export const updateFilter = (data) => schedWrite('update_filter', data);
export const deleteFilter = (filterId) => schedWrite('delete_filter', { filterId });
export const updateRecConfig = (updates, updatedBy) => schedWrite('upsert_rec_config', { updates, updatedBy });
export const getBatchId = () => schedWrite('get_batch_id');
export const upsertInterest = (staffId, interests) => schedWrite('upsert_interest', { staffId, interests });
export const saveOBResponse = (data) => schedWrite('save_ob_response', data);
export const saveOBConfig = (data) => schedWrite('save_ob_config', data);
export const saveHoliday = (data) => schedWrite('save_holiday', data);
export const deleteHoliday = (holidayId) => schedWrite('delete_holiday', { holidayId });
export const saveGlobalAssignment = (data) => schedWrite('save_global_assignment', data);
export const deleteGlobalAssignment = (assignmentId) => schedWrite('delete_global_assignment', { assignmentId });
// Placeholder assignments (item 1) — see api/schedWrite assign_placeholder for lifecycle.
export const upsertPlaceholder = (data) => schedWrite('upsert_placeholder', data);
export const assignPlaceholder = (data) => schedWrite('assign_placeholder', data);
export const deletePlaceholder = (placeholderId) => schedWrite('delete_placeholder', { placeholderId });
// Reverse of assignPlaceholder — see api/schedWrite convert_to_placeholder for lifecycle.
export const convertToPlaceholder = (data) => schedWrite('convert_to_placeholder', data);

// Custom jobs (item 2) — admin-managed firm-internal jobs (EBP Team, CPE, …).
// Both endpoints are gated server-side on isSuperUser.
export const upsertCustomJob = (data) => schedWrite('upsert_custom_job', data);
export const deleteCustomJob = (customJobId, opts) => schedWrite('delete_custom_job', { customJobId, ...(opts || {}) });

// ── Outlook calendar sync (Trello #133) ─────────────────────────────────────
// Toggle a staffer's opt-in (server enforces "your own StaffId only" unless super-user).
export const setOutlookOptIn = (staffId, optIn) => schedWrite('set_outlook_optin', { staffId, optIn });

// Fire-and-forget reconcile of affected staff's Outlook calendars. NEVER throws into the
// caller — a sync hiccup must not break a save (it self-heals on the next save / manual
// sync since the server reconcile is complete + idempotent). Returns the summary when
// awaited (the profile "Sync now" button does await it for the result toast). `purge`
// forces a cleanup pass for an opted-out mailbox (used by the opt-out toggle).
// Function declaration (hoisted) so the schedWrite() wrapper above can call it.
export function reconcileOutlook(staffIds, { purge = false } = {}) {
  const ids = (Array.isArray(staffIds) ? staffIds : [staffIds]).map(Number).filter(Boolean);
  if (!ids.length && !purge) return Promise.resolve(null);
  return fetch('/api/outlookSync?action=reconcile', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ staffIds: ids, purge }),
  })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
}

// PTO operations (via rprsPTO function)
async function ptoWrite(action, body = {}) {
  const url = `/api/rprsPTO?action=${encodeURIComponent(action)}`;
  const r = await fetchWithColdStartRetry(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }, { label: `ptoWrite(${action})` });
  if (!r.ok) {
    const err = await r.text();
    throw new Error(`ptoWrite(${action}) ${r.status}: ${err}`);
  }
  return r.json();
}

export const approvePTO = (leaveReqId) => ptoWrite('approve', { leaveReqId });
export const rejectPTO = (leaveReqId) => ptoWrite('reject', { leaveReqId });
export const requestPTO = ({ staffId, startDate, endDate, notes }) => ptoWrite('request', { staffId, startDate, endDate, notes });
export const cancelPTO = ({ leaveReqId, staffId }) => ptoWrite('cancel', { leaveReqId, staffId });
export const fetchLeaveBalance = (staffId) => ptoWrite('leave_balance', { staffId });

// ── IDD / Data Integrity ────────────────────────────────────────────────────

async function iddWrite(action, body = {}) {
  const url = `/api/rprsIDD?action=${encodeURIComponent(action)}`;
  const r = await fetchWithColdStartRetry(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }, { label: `iddWrite(${action})` });
  if (!r.ok) {
    const err = await r.text();
    throw new Error(`iddWrite(${action}) ${r.status}: ${err}`);
  }
  return r.json();
}

export const saveIDDDraft = (data) => iddWrite('save_draft', data);
export const submitIDDReview = (data) => iddWrite('submit', data);
// runAIRecommend: async start + poll. Avoids the SWA gateway's 230s wall
// that the old synchronous ai_recommend hit when Claude's response was
// large. ai_recommend_start INSERTs a pending AIJobs row, self-invokes the
// worker, and returns {aiJobId} in <2s. We then poll ai_recommend_status
// every POLL_INTERVAL_MS until status flips to 'done' or 'failed'.
//
// Signature is unchanged from the old version — callers receive the same
// {recommendations, model, jobCount, ...} shape once the job completes.
export async function runAIRecommend(data) {
  const startRes = await iddWrite('ai_recommend_start', data);
  const aiJobId = startRes.aiJobId;
  if (!aiJobId) throw new Error('ai_recommend_start did not return an aiJobId');

  const POLL_INTERVAL_MS = 3000;
  const MAX_WAIT_MS = 8 * 60 * 1000;  // 8 min — well past the slowest observed Claude run
  const startedAt = Date.now();

  // Brief warm-up wait before first poll so the worker has a moment to claim
  // the row and flip Status from pending → running. Polling sooner would
  // routinely catch the 'pending' state, which adds a wasted round-trip.
  await new Promise(r => setTimeout(r, 1500));

  while (true) {
    if (Date.now() - startedAt > MAX_WAIT_MS) {
      throw new Error(`AI recommendation timed out after ${Math.round(MAX_WAIT_MS / 60000)} minutes (aiJobId=${aiJobId})`);
    }
    const status = await iddWrite('ai_recommend_status', { aiJobId });
    if (status.status === 'done')   return status;
    if (status.status === 'failed') throw new Error(status.errorMessage || `AI recommendation failed (aiJobId=${aiJobId})`);
    // pending | running — keep polling
    await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
  }
}
// The newest finished AI run for (cycle, manager): the cycle-open pre-compute or the
// manager's own last Re-run. -> { recommendations: [...] | null, kind, completedAt, ... }
export const fetchAILatest = (data) => iddWrite('ai_latest', data);
// Super user: (re)run the cycle's batch pre-compute now (normally it runs on PrecomputeAt).
export const runIDDPrecomputeNow = (cycleName, force = false) => iddWrite('precompute_now', { cycleName, force });
export const openIDDCycle = (data) => iddWrite('open_cycle', data);
export const updateIDDCycle = (data) => iddWrite('update_cycle', data);
export const closeIDDCycle = (cycleName) => iddWrite('close_cycle', { cycleName });
export const reopenIDDCycle = (cycleName) => iddWrite('reopen_cycle', { cycleName });
export const clearIDDDraft = (peJobIndex, cycleName) => iddWrite('clear_draft', { peJobIndex, cycleName });
export const confirmIDDReview = (data) => iddWrite('confirm_review', data);
// Stage 28M: mark a job as Not Doing CY or Close & No Roll without filling
// dates/budget. Backed by IDDReviews.RemoveAction; PE writeback deferred.
export const removeIDDJob = (data) => iddWrite('remove_job', data);
export const revertIDDAudit = (auditId) => iddWrite('revert_audit', { auditId });
export const reopenUserSubmission = (cycleName, targetUser) => iddWrite('reopen_user_submission', { cycleName, targetUser });
export const remindUserReview = (cycleName, managerStaffId) => iddWrite('remind_user_review', { cycleName, managerStaffId });

// ── Non-Billable Budget Hours (Stage 28E) ───────────────────────────────────

async function nonBillWrite(action, body = {}) {
  const url = `/api/rprsNonBill?action=${encodeURIComponent(action)}`;
  const r = await fetchWithColdStartRetry(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }, { label: `nonBillWrite(${action})` });
  if (!r.ok) {
    const err = await r.text();
    throw new Error(`nonBillWrite(${action}) ${r.status}: ${err}`);
  }
  return r.json();
}

export const saveNonBillDraft = (data) => nonBillWrite('save_draft', data);
export const submitNonBill = (data) => nonBillWrite('submit', data);
export const reopenNonBillUser = (staffId, fiscalYear) => nonBillWrite('reopen_user_submission', { staffId, fiscalYear });
export const upsertNonBillCategory = (data) => nonBillWrite('upsert_category', data);
export const deleteNonBillCategory = (categoryKey) => nonBillWrite('delete_category', { categoryKey });
export const upsertNonBillRule = (data) => nonBillWrite('upsert_rule', data);
export const deleteNonBillRule = (ruleId) => nonBillWrite('delete_rule', { ruleId });
export const upsertNonBillStandard = (data) => nonBillWrite('upsert_standard', data);
export const deleteNonBillStandard = (departmentCode, levelCode) => nonBillWrite('delete_standard', { departmentCode, levelCode });
export const upsertNonBillOverride = (data) => nonBillWrite('upsert_override', data);
export const deleteNonBillOverride = (staffId) => nonBillWrite('delete_override', { staffId });
